import { TickMarkType } from "lightweight-charts";

const YMD_INPUT_RE = /^\d{4}-\d{2}-\d{2}$/;
const UTC_MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
/** Curated short list of common trading-session timezones. */
const COMMON_CHART_TIME_ZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Los_Angeles",
  "Europe/London",
  "Europe/Berlin",
  "Asia/Dubai",
  "Asia/Hong_Kong",
  "Asia/Tokyo",
  "Australia/Sydney",
];

const dateFromChartTime = (timeValue) => {
  if (typeof timeValue === "number" && Number.isFinite(timeValue)) {
    return new Date(timeValue * 1000);
  }

  if (typeof timeValue === "string") {
    const trimmed = timeValue.trim();
    if (!trimmed) {
      return null;
    }
    if (YMD_INPUT_RE.test(trimmed)) {
      return new Date(`${trimmed}T00:00:00Z`);
    }
    const parsed = Date.parse(trimmed);
    return Number.isFinite(parsed) ? new Date(parsed) : null;
  }

  if (
    timeValue &&
    typeof timeValue === "object" &&
    "year" in timeValue &&
    "month" in timeValue &&
    "day" in timeValue
  ) {
    return new Date(Date.UTC(timeValue.year, timeValue.month - 1, timeValue.day));
  }

  return null;
};

/** Sentinel value meaning "use UTC as the chart default". */
export const DEFAULT_TIME_ZONE_VALUE = "UTC";

const resolveTimeZone = (timeZone) => timeZone || "UTC";

const resolveLocalTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

const pad2 = (value) => String(value).padStart(2, "0");

const formatUtcTooltipTime = (date) =>
  `${UTC_MONTH_SHORT[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}, ${pad2(
    date.getUTCHours()
  )}:${pad2(date.getUTCMinutes())}`;

const formatUtcTickMark = (date, tickMarkType) => {
  switch (tickMarkType) {
    case TickMarkType.Year:
      return String(date.getUTCFullYear());
    case TickMarkType.Month:
      return `${UTC_MONTH_SHORT[date.getUTCMonth()]} ${String(date.getUTCFullYear()).slice(-2)}`;
    case TickMarkType.DayOfMonth:
      return `${pad2(date.getUTCDate())} ${UTC_MONTH_SHORT[date.getUTCMonth()]}`;
    case TickMarkType.Time:
      return `${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}`;
    case TickMarkType.TimeWithSeconds:
      return `${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}:${pad2(
        date.getUTCSeconds()
      )}`;
    default:
      return null;
  }
};

const formatterForZone = (timeZone, locale, options) => {
  const resolved = resolveTimeZone(timeZone);
  try {
    return new Intl.DateTimeFormat(locale, { ...options, timeZone: resolved });
  } catch {
    // An unrecognized zone must never silently fall back to whatever zone this browser
    // happens to be running in (that produced a real bug: an invalid IANA name in the
    // zone list rendered as the visitor's local offset, mislabeled as the intended city).
    // UTC is the one fallback that's always correct for the underlying epoch-second value.
    return new Intl.DateTimeFormat(locale, { ...options, timeZone: "UTC" });
  }
};

export const CHART_TIME_ZONE_OPTIONS = (() => {
  const seen = new Set();
  const list = [];
  for (const zone of COMMON_CHART_TIME_ZONES) {
    if (!seen.has(zone)) {
      seen.add(zone);
      list.push(zone);
    }
  }
  const localTimeZone = resolveLocalTimeZone();
  if (localTimeZone && !seen.has(localTimeZone)) {
    list.push(localTimeZone);
  }
  return list;
})();

/** Default timezone for the chart control until the user picks another. */
export const DEFAULT_CHART_TIME_ZONE = DEFAULT_TIME_ZONE_VALUE;

export const formatChartTimeValue = (timeValue, timeZone, options, locale) => {
  const date = dateFromChartTime(timeValue);
  if (!date) {
    return "";
  }
  return formatterForZone(timeZone, locale, options).format(date);
};

export const formatChartTooltipTime = (timeValue, timeZone, locale) => {
  const date = dateFromChartTime(timeValue);
  if (!date) {
    return "";
  }
  if (resolveTimeZone(timeZone).toUpperCase() !== "UTC") {
    return formatChartTimeValue(
      timeValue,
      timeZone,
      {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      },
      locale
    );
  }
  return formatUtcTooltipTime(date);
};

export const formatChartTickMark = (timeValue, tickMarkType, locale, timeZone) => {
  const date = dateFromChartTime(timeValue);
  if (!date) {
    return "";
  }
  if (resolveTimeZone(timeZone).toUpperCase() !== "UTC") {
    switch (tickMarkType) {
      case TickMarkType.Year:
        return formatChartTimeValue(timeValue, timeZone, { year: "numeric" }, locale);
      case TickMarkType.Month:
        return formatChartTimeValue(timeValue, timeZone, { month: "short", year: "2-digit" }, locale);
      case TickMarkType.DayOfMonth:
        return formatChartTimeValue(timeValue, timeZone, { day: "2-digit", month: "short" }, locale);
      case TickMarkType.Time:
        return formatChartTimeValue(
          timeValue,
          timeZone,
          { hour: "2-digit", minute: "2-digit", hour12: false },
          locale
        );
      case TickMarkType.TimeWithSeconds:
        return formatChartTimeValue(
          timeValue,
          timeZone,
          { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false },
          locale
        );
      default:
        return null;
    }
  }

  return formatUtcTickMark(date, tickMarkType);
};

export const formatChartTimeZoneLabel = (timeZone, locale) => {
  if (resolveTimeZone(timeZone).toUpperCase() === "UTC") {
    // UTC is the only option that never runs an Intl timezone conversion (see
    // formatChartTooltipTime/formatChartTickMark) — it prints the stored epoch-second
    // value's calendar fields verbatim, so it's also "the original timestamp, unshifted."
    return "UTC (Original, no conversion)";
  }
  try {
    const parts = formatterForZone(timeZone, locale, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZoneName: "shortOffset",
    }).formatToParts(new Date());
    const offset = parts.find((part) => part.type === "timeZoneName")?.value;
    return offset ? `${offset.replace("GMT", "UTC")} · ${timeZone}` : timeZone;
  } catch {
    return timeZone;
  }
};
